type Source = {
  srcSet?: string;
  media?: string;
  type: string;
  format: string;
  width?: number;
  breakpoint: string;
  fit?: string;
}

const VIEWPORTS: Record<string, string | undefined> = {
  'default': undefined,
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
}

const BREAKPOINTS_SORT = [ 'xl', 'lg', 'md', 'sm', 'default' ];
const FORMAT_SORT = [ 'webp', 'png', 'jpg' ];

const parseFormats = ( config: string ) => {
  return config.match( /format-(\w+)/g )
    ?.map( f => f.replace( 'format-', '' ) )
    ?.sort( ( a ) => a === 'webp' ? -1 : 1 );
}

const parseBreakpoints = ( config: string ) => {
  const breakpoints: Array<string> = [];
  const breakpointsRegex = /(?:(sm|md|lg|xl):)?(?:(?:(w|src)-\[(.*?)\])|(?:(fit)-([a-zA-Z-]+)))/g;
  let match;
  while ( ( match = breakpointsRegex.exec( config ) ) !== null ) {
    breakpoints.push( match[1] || 'default' );
  }

  return breakpoints
    .filter( ( item, index ) => breakpoints.indexOf( item ) === index )
    .sort( ( a, b ) => {
      const breakpointIndexA = BREAKPOINTS_SORT.indexOf( a );
      const breakpointIndexB = BREAKPOINTS_SORT.indexOf( b );

      return breakpointIndexB - breakpointIndexA;
    } );
}

const parseUnit = ( config: string, unit: string, breakpoint?: string ) => {
  const pattern = breakpoint ?
    `${ breakpoint }:${ unit }-\\[(.*?)\\]` :
    `(?:^|[^:])${ unit }-\\[(.*?)\\]`;
  const regex = new RegExp( pattern, 'g' );
  const match = regex.exec( config );
  return match ? match[1] : null;
}

const parseFit = ( config: string, breakpoint?: string ) => {
  const pattern = breakpoint ?
    `${ breakpoint }:fit-(\\w+)` :
    '(?:^|[^:])fit-(\\w+)';
  const regex = new RegExp( pattern, 'g' );
  const match = regex.exec( config );
  return match ? match[1] : null;
}

export const parseImageConfig = ( config: string | Array<string | undefined> ) => {
  const configuration = Array.isArray( config ) ? config.join( ' ' ) : config;
  if ( configuration == null || configuration.length === 0 ) {
    return null;
  }
  const formats = parseFormats( configuration );
  const breakpoints = parseBreakpoints( configuration );

  const sources: Array<Source> = [];
  formats?.forEach( ( format ) => {

    breakpoints.forEach( ( breakpoint ) => {
      const source: Source = {
        breakpoint,
        type: `image/${ format }`,
        format,
        media: VIEWPORTS[ breakpoint ] ? `(min-width: ${ VIEWPORTS[ breakpoint ] })` : undefined
      };

      const width = parseUnit( configuration, 'w', breakpoint === 'default' ? undefined : breakpoint );
      if ( width ) {
        source.width = Number( width.replace( 'px', '' ) );
      }

      const srcSet = parseUnit( configuration, 'src', breakpoint === 'default' ? undefined : breakpoint );
      if ( srcSet ) {
        source.srcSet = srcSet;
      }

      const fit = parseFit( configuration, breakpoint === 'default' ? undefined : breakpoint );
      if ( fit ) {
        source.fit = fit;
      }

      const prevSource = sources[ sources.length - 1 ];
      sources.push( {
        ...( prevSource?.format === source?.format ? prevSource : undefined ),
        ...source
      } );
    } )
  } );

  sources.sort( ( a, b ) => {
    const formatIndexA = FORMAT_SORT.indexOf( a.format );
    const formatIndexB = FORMAT_SORT.indexOf( b.format );

    if ( formatIndexA !== formatIndexB ) {
      return formatIndexA - formatIndexB;
    }

    const breakpointIndexA = BREAKPOINTS_SORT.indexOf( a.breakpoint );
    const breakpointIndexB = BREAKPOINTS_SORT.indexOf( b.breakpoint );

    return breakpointIndexA - breakpointIndexB;
  } );

  return sources;
}
